import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useStore } from '../context/StoreContext';

const VideoCard = ({ video, onPlay }) => {
  const { user } = useStore();
  const isLocked = video.accessLevel === 'pro' && !user.isSubscribed;

  const handleClick = () => {
    if (isLocked) {
      window.open('https://pages.razorpay.com/rcktalgonewplan', '_blank');
    } else {
      onPlay(video);
    }
  };

  return (
    <motion.div 
      // Removed fixed min-w to allow the grid/flex-parent to strictly control the width.
      // aspect-video + absolute inset-0 image guarantees perfect 16:9 
      className="relative group w-full aspect-video rounded-md overflow-hidden cursor-pointer bg-dark-card transition-all duration-300 ease-out z-0 hover:z-50"
      whileHover={{ scale: 1.05, y: -5 }}
      transition={{ duration: 0.3 }}
      onClick={handleClick}
    >
      {/* Absolute positioning ensures the image is strictly bound to the 16:9 container */}
      <img 
        src={video.thumbnailUrl} 
        alt={video.title} 
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
      />
      
      {/* Permanent Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Top Left Tags & LIVE Badge */}
      <div className="absolute top-2 left-2 flex flex-col gap-1.5 items-start z-10">
        {video.isLive && (
          <div className="bg-danger text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm flex items-center space-x-1">
            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
            <span>LIVE</span>
          </div>
        )}
      </div>

      {/* Top Right Lock Icon */}
      {isLocked && (
        <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm p-1.5 rounded-full border border-white/10 group-hover:bg-rocket transition-colors shadow-lg z-10">
          <SafeIcon name="Lock" className="w-3.5 h-3.5 text-white" />
        </div>
      )}

      {/* Default State Content (Visible when NOT hovering) */}
      <div className="absolute inset-0 flex flex-col justify-end p-3 md:p-4 group-hover:opacity-0 transition-opacity duration-300">
        <h4 className="text-white font-bold text-[15px] leading-tight drop-shadow-md line-clamp-1">{video.title}</h4>
        {video.progress > 0 && typeof video.progress !== 'undefined' && (
          <div className="w-full bg-white/20 h-1 mt-2 rounded-full overflow-hidden">
            <div className="bg-danger h-full" style={{ width: `${video.progress}%` }}></div>
          </div>
        )}
      </div>

      {/* Hover State Detail Overlay (Netflix Style Expansion Preview) */}
      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black via-black/95 to-transparent flex flex-col justify-end opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-4 group-hover:translate-y-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors shadow-lg">
              <SafeIcon name="Play" className="w-4 h-4 text-black ml-1 fill-current" />
            </div>
            {isLocked && (
              <div className="w-8 h-8 rounded-full border border-white/40 flex items-center justify-center hover:border-white transition-colors">
                <SafeIcon name="Lock" className="w-3.5 h-3.5 text-white" />
              </div>
            )}
          </div>
          {video.duration && (
            <span className="text-white text-xs font-semibold">{video.duration}</span>
          )}
        </div>
        <h4 className="text-white font-bold text-[15px] leading-tight mb-1">{video.title}</h4>
        <div className="flex items-center space-x-2 mb-2">
          <span className="text-green-400 font-bold text-[11px]">98% Match</span>
          <span className="text-gray-300 text-[11px] border border-gray-500 px-1 rounded uppercase">HD</span>
        </div>
        
        {/* Category Pill Tags */}
        {video.tags && video.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {video.tags.map(tag => (
              <span key={tag} className="text-[10px] text-gray-300 font-medium flex items-center">
                <span className="w-1 h-1 bg-gray-500 rounded-full mr-1.5 inline-block"></span>
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default VideoCard;